import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@/components/Link";
import { reviewThankYouCopy } from "@/components/reviewThankYouCopy";
import { Textarea } from "@/components/ui/textarea";
import StarRating from "@/components/ui/StarRating";
import { ratingFactorLabels } from "@/types/review";
import PushSoftAsk from "@/components/notifications/PushSoftAsk";
import WhatsappOptInCard, {
  fetchNotificationPreferences,
  isWhatsappPromptVisible,
} from "@/components/notifications/WhatsappOptInCard";
import { useAuth } from "@/contexts/AuthContext";
import { NOTIFICATION_QUERY_KEYS } from "@/lib/notificationUi";
import { isAccountYoung, shouldShowThankYouWhatsappAsk } from "@/lib/onboardingUi";
import { NEXT_TIER_NUDGE, REVIEW_COMPONENTS } from "@shared/gamification";

// The inline state that replaces the review form after a successful submit, on BOTH submit
// surfaces (the store page's ReviewsSection and /rate/@slug). Presentational only: no fetching,
// no mutation, no Dialog. A submit that ends in a toast is a dead end; this turns it into a
// second-session hook.
//
// It is also the FIRST place a user ever sees points on the web. `awardForReview` has until now
// announced itself only through `sendMessageToUser`, a chat message, invisible on site.
//
// The upsell rows below are the payoff of the ledger's per-component unique index: adding a photo
// here re-runs `awardForReview` through the EXISTING update/upload endpoints and pays exactly the
// newly earned component, nothing more. No new backend endpoint exists for this, on purpose.
//
// NO POSTHOG IMPORT HERE, deliberately. CLAUDE.md forbids capturing directly from a component, and
// the two callers already hold the store document the standard props are built from. The component
// takes `onSearchClick` / `onUpsell` callbacks and each caller supplies its own `surface` plus the
// typed helpers.
//
// NO NETWORK CALL HERE either: the inline editors take injected async handlers
// (`onAddText` / `onAddPhoto` / `onAddFactors`) and the caller owns the mutation, the cache
// invalidation and the balance re-read. The one exception is the soft-ask slot's gate (49-15, S3):
// it reads the SHARED notification-preferences cache entry (same key the WhatsApp card and
// PushSoftAsk use, so no extra request once either has mounted), only for young accounts.
//
// The rating -> tone mapping lives in `reviewThankYouCopy` and is resolved here, never in a caller:
// two callers each doing their own bucketing is how the two surfaces drift apart.

const font = "var(--ys-font)";

// Same shapes as RateStore.tsx so the card reads identically on both surfaces.
const cardStyle: React.CSSProperties = {
  background: "var(--ys-paper)",
  border: "1px solid var(--ys-rule)",
  borderRadius: "var(--ys-r-md)",
  boxShadow: "var(--ys-shadow-2)",
  padding: 24,
  direction: "rtl",
  fontFamily: font,
  textAlign: "center",
};

const headingStyle: React.CSSProperties = {
  fontFamily: font,
  fontSize: 20,
  fontWeight: 600,
  color: "var(--ys-ink)",
  margin: 0,
};

const bodyStyle: React.CSSProperties = {
  fontFamily: font,
  fontSize: 14,
  color: "var(--ys-ink-2)",
  margin: 0,
};

const primaryBtnStyle: React.CSSProperties = {
  background: "var(--ys-primary)",
  color: "#fff",
  border: "none",
  borderRadius: "var(--ys-r)",
  padding: "12px 20px",
  minHeight: 44,
  fontFamily: font,
  fontSize: 16,
  fontWeight: 600,
  cursor: "pointer",
};

const smallBtnStyle: React.CSSProperties = {
  ...primaryBtnStyle,
  padding: "8px 16px",
  minHeight: 36,
  fontSize: 14,
};

export type ThankYouUpsellElement = "text" | "photo" | "factors";

export type ReviewThankYouUpsell = {
  element: ThankYouUpsellElement;
  points: number;
};

// The shared nudge copy, keyed by the element the caller talks in. Reusing the strength meter's
// strings is the point: the writer is being offered the same thing in the same words they just
// declined in the form.
const ELEMENT_ACTION: Record<ThankYouUpsellElement, string> = {
  text: "review_text",
  photo: "review_photo",
  factors: "review_factors",
};

const nudgeFor = (element: ThankYouUpsellElement): string =>
  REVIEW_COMPONENTS.find((c) => c.action === ELEMENT_ACTION[element])?.nudgeAr ?? "";

export type ReviewThankYouProps = {
  rating: number;
  storeHref: string;
  onSearchClick?: () => void;
  // Defaults to the browse page. A plain link is the correct reuse boundary: SearchAutocomplete is
  // a controlled dropdown (`{ inputValue, open, onClose }`) that expects a host input, so dropping
  // it in here would mean building a second search input to drive it.
  searchHref?: string;
  // Points ACTUALLY credited, read back from the server rather than taken from the form's preview:
  // the daily ceiling uses a UTC day boundary, so a user near the cap late in the Egyptian evening
  // can be credited less than the preview promised.
  earnedPoints?: number;
  // Omitted at tier 5 by the caller, since there is nothing left to reach.
  nextTier?: { ar: string; pointsToNext: number };
  upsells?: ReviewThankYouUpsell[];
  onUpsell?: (element: ThankYouUpsellElement) => void;
  onAddText?: (text: string) => Promise<void>;
  onAddPhoto?: (files: File[]) => Promise<void>;
  onAddFactors?: (factors: Record<string, number>) => Promise<void>;
  // The category's factor keys, loaded by the caller. Without them the factors row is not offered:
  // writing factor keys that the store's category does not use would produce a breakdown nobody
  // renders.
  factorKeys?: string[];
};

const ReviewThankYou: React.FC<ReviewThankYouProps> = ({
  rating,
  storeHref,
  onSearchClick,
  searchHref = "/stores",
  earnedPoints,
  nextTier,
  upsells,
  onUpsell,
  onAddText,
  onAddPhoto,
  onAddFactors,
  factorKeys = [],
}) => {
  const copy = reviewThankYouCopy(rating);

  const [openElement, setOpenElement] = useState<ThankYouUpsellElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [draftText, setDraftText] = useState("");
  const [draftFactors, setDraftFactors] = useState<Record<string, number>>({});
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // S3 (49-15): the reviewer's single second WhatsApp ask. Review-trigger signups who declined on
  // the finish screen and are still eligible (shouldShowThankYouWhatsappAsk); accept/dismiss clears
  // eligibility server side, so it never shows a third time.
  const { user } = useAuth();
  const young = !!user && isAccountYoung({ createdAt: user.createdAt });
  const prefsQuery = useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.preferences,
    queryFn: fetchNotificationPreferences,
    enabled: young,
    retry: false,
  });
  const showWa = young && shouldShowThankYouWhatsappAsk({ user, prefs: prefsQuery.data, now: new Date() });
  const waOnScreen = showWa && isWhatsappPromptVisible(prefsQuery.data);
  // One card at a time: once the WhatsApp card has shown here, the push ask waits for the NEXT
  // thank-you, even after the prefs refetch that follows accept/dismiss turns showWa off.
  const [waTaken, setWaTaken] = useState(false);
  React.useEffect(() => {
    if (waOnScreen) setWaTaken(true);
  }, [waOnScreen]);

  // A row is only offered when the caller can actually satisfy it.
  const handlerFor: Record<ThankYouUpsellElement, boolean> = {
    text: !!onAddText,
    photo: !!onAddPhoto,
    factors: !!onAddFactors && factorKeys.length > 0,
  };
  const rows = (upsells ?? []).filter((u) => handlerFor[u.element]);

  const open = (element: ThankYouUpsellElement) => {
    setOpenElement((current) => (current === element ? null : element));
    onUpsell?.(element);
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
      setOpenElement(null);
      setDraftText("");
      setDraftFactors({});
    } finally {
      // The caller owns error reporting (both surfaces already toast). Clearing `busy` in a
      // `finally` is what keeps a failed upsell retryable rather than permanently disabled.
      setBusy(false);
    }
  };

  const nextTierLine =
    nextTier && nextTier.pointsToNext > 0
      ? // 40 is what a complete written review pays, so anything at or under it is genuinely one
        // review away and saying so is more concrete than a points figure.
        nextTier.pointsToNext <= 40
        ? NEXT_TIER_NUDGE.oneReviewAway({ tierAr: nextTier.ar })
        : NEXT_TIER_NUDGE.pointsAway({ points: nextTier.pointsToNext, tierAr: nextTier.ar })
      : null;

  return (
    <div style={cardStyle}>
      <h2 style={headingStyle}>{copy.title}</h2>
      <p style={{ ...bodyStyle, marginTop: 8 }}>{copy.body}</p>

      {typeof earnedPoints === "number" && earnedPoints > 0 && (
        <p
          style={{
            ...bodyStyle,
            marginTop: 8,
            color: "var(--ys-primary)",
            fontWeight: 600,
            fontSize: 16,
          }}
        >
          كسبت {earnedPoints} نقطة على تقييمك
        </p>
      )}

      {nextTierLine && (
        <p style={{ ...bodyStyle, marginTop: 4 }}>{nextTierLine}</p>
      )}

      <p style={{ ...bodyStyle, marginTop: 8, color: "var(--ys-ink-3)" }}>{copy.reminder}</p>

      {rows.length > 0 && (
        <div
          style={{
            marginTop: 20,
            paddingTop: 16,
            borderTop: "1px solid var(--ys-rule)",
            textAlign: "right",
          }}
        >
          <p style={{ ...bodyStyle, fontWeight: 600, color: "var(--ys-ink)" }}>
            كمّل تقييمك وتكسب نقط زيادة
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
            {rows.map((row) => (
              <div key={row.element}>
                <button
                  type="button"
                  onClick={() => open(row.element)}
                  disabled={busy}
                  style={{
                    width: "100%",
                    textAlign: "right",
                    background: "var(--ys-bg-2)",
                    border: "1px solid var(--ys-rule)",
                    borderRadius: "var(--ys-r)",
                    padding: "10px 12px",
                    minHeight: 44,
                    fontFamily: font,
                    fontSize: 14,
                    color: "var(--ys-ink)",
                    cursor: busy ? "default" : "pointer",
                  }}
                >
                  {nudgeFor(row.element)}
                </button>

                {openElement === row.element && (
                  <div style={{ marginTop: 8 }}>
                    {row.element === "text" && (
                      <>
                        <Textarea
                          value={draftText}
                          onChange={(e) => setDraftText(e.target.value)}
                          placeholder="اكتب تجربتك مع هذا المتجر..."
                          maxLength={1000}
                          className="min-h-[90px] font-arabic"
                          dir="rtl"
                        />
                        <button
                          type="button"
                          style={{ ...smallBtnStyle, marginTop: 8 }}
                          disabled={busy || !draftText.trim()}
                          onClick={() => run(() => onAddText!(draftText.trim()))}
                        >
                          {busy ? "جارِ الحفظ…" : "حفظ"}
                        </button>
                      </>
                    )}

                    {row.element === "photo" && (
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []).filter((f) =>
                              f.type.startsWith("image/")
                            );
                            if (fileInputRef.current) fileInputRef.current.value = "";
                            if (files.length > 0) void run(() => onAddPhoto!(files));
                          }}
                        />
                        <button
                          type="button"
                          style={smallBtnStyle}
                          disabled={busy}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {busy ? "جارِ الرفع…" : "اختر صورة"}
                        </button>
                      </>
                    )}

                    {row.element === "factors" && (
                      <>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {factorKeys.map((factor) => (
                            <div
                              key={factor}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <span style={{ ...bodyStyle, color: "var(--ys-ink)" }}>
                                {ratingFactorLabels[factor] || factor}
                              </span>
                              <StarRating
                                rating={draftFactors[factor] || 0}
                                onRatingChange={(value) =>
                                  setDraftFactors((prev) => ({ ...prev, [factor]: value }))
                                }
                              />
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          style={{ ...smallBtnStyle, marginTop: 8 }}
                          disabled={
                            busy ||
                            Object.values(draftFactors).filter((v) => v > 0).length === 0
                          }
                          onClick={() => run(() => onAddFactors!(draftFactors))}
                        >
                          {busy ? "جارِ الحفظ…" : "حفظ"}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
          marginTop: 20,
        }}
      >
        <Link to={searchHref} style={{ textDecoration: "none" }} onClick={onSearchClick}>
          <button type="button" style={primaryBtnStyle}>
            {copy.searchCta}
          </button>
        </Link>
        <Link
          to={storeHref}
          style={{
            fontFamily: font,
            fontSize: 14,
            fontWeight: 600,
            color: "var(--ys-ink-2)",
            textDecoration: "underline",
          }}
        >
          {copy.storeCta}
        </Link>
      </div>

      {/* S3 second WhatsApp ask (49-15) above the D-17 push soft ask; never both at once. */}
      <div style={{ marginTop: 20 }}>
        {showWa && <WhatsappOptInCard surface="review_thank_you" source="onboarding" copyVariant="onboarding" />}
        {/* D-17 soft ask: self-hides unless push is actually offerable on this device. */}
        {!waOnScreen && !waTaken && <PushSoftAsk surface="review_thank_you" />}
      </div>
    </div>
  );
};

export default ReviewThankYou;
